import styles from "./markdown.module.scss";
import type { IProps } from "./markdown.types";
import { Tooltip } from "../tooltip";
import ReactMarkdown from "react-markdown";
import React, { forwardRef, useMemo } from "react";
import remarkGfm from "remark-gfm";
import { preprocessString, preserveNewlines } from "./markdown.utilities";
import { Text } from "../text";
import { Anchor } from "../anchor";
import classNames from "classnames";
import { Box } from "../box";
import { List } from "../list";
import { Image } from "../image";

const STRONG_CLASSNAME_REGEX = /issueStart\[(.*),(.*)\]([\s\S]*)issueEnd/;
const CLASSNAME_REGEX = /classNameStart\[(.*),(.*)\]([\s\S]*)classNameEnd/;

/**
 * Markdown component for rendering markdown strings with custom styling and tooltip support.
 * @param {object} props.textProps - Props to pass to Text components.
 * @param {object} props.listProps - Props to pass to List components.
 * @param {object} props.listItemProps - Props to pass to ListItem components.
 * @param {string} props.linkTarget - Target attribute for links (default: "_blank").
 * @param {object} props.anchorProps - Props to pass to Anchor components ommiting target and rel as they are handled by the component.
 * @param {string} props.markdownString - The markdown string to render.
 * @returns {JSX.Element} The Markdown component.
 */
const Markdown = forwardRef<HTMLDivElement, IProps>(function Markdown(
    {
        textProps,
        listProps,
        imageProps,
        listItemProps,
        linkTarget = "_blank",
        markdownString,
        anchorProps,
        ...otherProps
    },
    ref
) {
    const content = useMemo(() => {
        return preserveNewlines(preprocessString(markdownString));
    }, [markdownString]);

    const ConditionalImagePreview = useMemo(() => {
        if (imageProps && imageProps.preview) {
            return Image.Preview;
        }

        return Box;
    }, [imageProps]);

    const ConditionalImage = useMemo(() => {
        if (imageProps && imageProps.preview) {
            return Image.Preview.Image;
        }

        return Image;
    }, [imageProps]);

    return (
        <Box {...otherProps}>
            <ConditionalImagePreview>
                <ReactMarkdown
                    components={{
                        img: ({ ref, ...props }) => {
                            const { src } = props;
                            if (typeof src === "undefined") return null;

                            return <ConditionalImage src={src} {...props} {...imageProps} />;
                        },
                        p: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            return (
                                <Text {...textProps} {...otherProps}>
                                    {children}
                                </Text>
                            );
                        },
                        pre: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            return (
                                <Text component="pre" style={{ whiteSpace: "pre-wrap" }} {...textProps} {...otherProps}>
                                    {children}
                                </Text>
                            );
                        },
                        code: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            return (
                                <Text component="code" {...textProps} {...otherProps}>
                                    {children}
                                </Text>
                            );
                        },
                        ul: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            return (
                                <List {...listProps} {...otherProps}>
                                    {children}
                                </List>
                            );
                        },
                        ol: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            // Force a decimal marker on ordered lists. Callers commonly pass listProps={listStyleType:
                            // "disc"} to restore markers despite the global reset; without this override, ordered lists
                            // would render with disc bullets and lose their numbering.
                            return (
                                <List {...listProps} listStyleType="decimal" type={"ordered" as any} {...otherProps}>
                                    {children}
                                </List>
                            );
                        },
                        li: ({ children, ref, ...otherProps }) => {
                            if (typeof children === "undefined") return null;

                            return (
                                <List.Item {...listItemProps} {...otherProps}>
                                    {children}
                                </List.Item>
                            );
                        },
                        a: ({ node, ref, ...props }) => {
                            const { children, ...otherProps } = props;
                            if (typeof children === "undefined") return null;

                            return (
                                <Anchor
                                    {...textProps}
                                    {...otherProps}
                                    {...anchorProps}
                                    style={{ wordBreak: "break-word" }}
                                    target={linkTarget}
                                    rel={linkTarget === "_blank" ? "noreferrer noopener" : undefined}
                                >
                                    {children}
                                    {linkTarget === "_blank" && (
                                        <Text {...textProps} span className={styles["screen-reader-text"]}>
                                            {" "}
                                            this link will open in a new tab
                                        </Text>
                                    )}
                                </Anchor>
                            );
                        },
                        strong: ({ children }) => {
                            if (typeof children === "undefined") return null;

                            const { className, ...otherTextProps } = textProps || {};
                            // Allow a class name and description with the syntax:
                            // "**issueStart[className,description]textissueEnd**".
                            const textContent = React.Children.toArray(children).join("");
                            const classNameMatch = textContent.match(CLASSNAME_REGEX);
                            if (classNameMatch) {
                                return (
                                    <Tooltip
                                        label={classNameMatch[2]}
                                        disabled={
                                            classNameMatch.length < 3 ||
                                            typeof classNameMatch[2] === "undefined" ||
                                            classNameMatch[2].trim() === ""
                                        }
                                    >
                                        <Text
                                            span
                                            className={classNames(className, classNameMatch[1])}
                                            {...otherTextProps}
                                        >
                                            {classNameMatch[3]}
                                        </Text>
                                    </Tooltip>
                                );
                            }

                            const match = textContent.match(STRONG_CLASSNAME_REGEX);
                            if (match) {
                                return (
                                    <Tooltip
                                        label={match[2]}
                                        disabled={
                                            match.length < 3 ||
                                            typeof match[2] === "undefined" ||
                                            match[2].trim() === ""
                                        }
                                    >
                                        <Text
                                            variant="semibold"
                                            span
                                            className={classNames(className, match[1])}
                                            role="strong"
                                            {...otherTextProps}
                                        >
                                            {match[3]}
                                        </Text>
                                    </Tooltip>
                                );
                            }

                            return (
                                <Text variant="semibold" {...textProps} span>
                                    {children}
                                </Text>
                            );
                        },
                    }}
                    remarkPlugins={[remarkGfm]}
                >
                    {content}
                </ReactMarkdown>
            </ConditionalImagePreview>
        </Box>
    );
});

export { Markdown, CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX };
